
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import CategoryTabs from '@/components/media-monitoring/CategoryTabs';
import ArticleList from '@/components/media-monitoring/ArticleList';
import { useMediaMonitoring } from '@/hooks/useMediaMonitoring';

const MediaMonitoring = () => {
  const { companyName } = useParams<{ companyName: string }>();
  const decodedCompanyName = decodeURIComponent(companyName || '');
  
  const {
    isLoading,
    selectedCategory,
    articles,
    error,
    handleCategoryChange,
    handleRefresh
  } = useMediaMonitoring(decodedCompanyName);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex justify-between items-center">
            <Button 
              variant="ghost" 
              asChild
              className="text-redflags-gray hover:text-redflags-dark"
            >
              <Link to={`/companies/${companyName}`} className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to {decodedCompanyName} Profile
              </Link>
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
          </div>
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-redflags-dark mb-2">Media Monitoring</h1>
            <p className="text-redflags-gray mb-4">
              News and media analysis for {decodedCompanyName}
            </p>
            
            <Card className="border border-redflags-light/70 mb-6">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-600 mb-2">
                  Our AI-powered media monitoring scans recent news articles, press releases, and public information
                  about {decodedCompanyName} to identify potential risk indicators.
                </p>
                <p className="text-sm text-gray-600">
                  Data is refreshed regularly and may contain information from various sources.
                </p>
              </CardContent>
            </Card>
            
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <CategoryTabs 
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
            >
              <ArticleList 
                articles={articles}
                isLoading={isLoading}
              />
            </CategoryTabs>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default MediaMonitoring;
